import { getIntlTranslation } from '@/utils/intl';
import { html } from '@codemirror/lang-html';
import { Prec, RangeSetBuilder } from '@codemirror/state';
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
} from '@codemirror/view';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'umi';
import VariableSuggestionPopup, {
  SuggestionAnchor,
} from '../VariableSuggestionPopup';
import { useTranslationEntries } from '../useTranslationEntries';
import { useVariableSuggestions } from '../useVariableSuggestions';
import { findFragmentReferenceRanges } from './insertion';
import { EmailVariableGroup } from './types';

import styles from './index.less';

/** Imperative API used by the insert-variable / insert-block toolbar buttons. */
export interface TwigCodeEditorHandle {
  /** Replaces the current selection with the snippet and refocuses. */
  insertAtCursor: (snippet: string) => void;
  /** Selects `from`–`to`, scrolls it into view and focuses the editor. */
  reveal: (from: number, to: number) => void;
}

/** The caret, or the highlighted range when there is one. */
export interface EditorSelection {
  from: number;
  to: number;
  text: string;
}

interface TwigCodeEditorProps {
  /** Optional so antd Form.Item can inject it as a controlled field. */
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  /**
   * Called when a `{% include/extends 'key' %}` reference is Cmd/Ctrl-clicked,
   * so the host can open that building block for editing.
   */
  onOpenReference?: (key: string) => void;
  /**
   * Offered inline as soon as `{{` is typed. Omitted, the list still offers the
   * partner's saved texts — those come from the catalog, not from this prop.
   */
  variableGroups?: EmailVariableGroup[];
  /**
   * Every caret move and edit, so a host can offer actions on what is
   * highlighted. Fires on document changes too: an edit moves the caret without
   * always changing the selection object.
   */
  onSelectionChange?: (selection: EditorSelection) => void;
}

/**
 * How far back from the caret the `{{` is looked for. A variable path is short, and
 * scanning the whole document on every keystroke would be work proportional to a body
 * that can run to tens of kilobytes.
 */
const TRIGGER_LOOKBACK = 200;

/**
 * Mutable holder for the latest open-callback so the CodeMirror extension
 * (built once per mount) always calls the current handler without the editor
 * being torn down and rebuilt when the prop identity changes.
 */
interface ReferenceCallbackRef {
  current: ((key: string) => void) | undefined;
}

function buildReferenceDecorations(
  view: EditorView,
  mark: Decoration,
): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    for (const range of findFragmentReferenceRanges(text)) {
      builder.add(from + range.from, from + range.to, mark);
    }
  }
  return builder.finish();
}

/**
 * CodeMirror extension: highlights every fragment reference key as a clickable
 * chip and opens it on Cmd/Ctrl-click (plain clicks still position the caret,
 * so the key stays editable). The referenced key is read straight from the
 * document text at the click position, so it always reflects the current
 * source. `hoverHint` becomes the chip's native tooltip.
 */
function fragmentReferenceExtension(
  callbackRef: ReferenceCallbackRef,
  hoverHint: string,
) {
  const mark = Decoration.mark({
    class: styles.fragmentRef,
    attributes: { title: hoverHint },
  });

  const plugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = buildReferenceDecorations(view, mark);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = buildReferenceDecorations(update.view, mark);
        }
      }
    },
    { decorations: (plugin) => plugin.decorations },
  );

  const clickHandler = EditorView.domEventHandlers({
    mousedown(event, view) {
      if (!(event.metaKey || event.ctrlKey) || !callbackRef.current) {
        return false;
      }
      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos == null) return false;
      const line = view.state.doc.lineAt(pos);
      const offsetInLine = pos - line.from;
      for (const range of findFragmentReferenceRanges(line.text)) {
        if (offsetInLine >= range.from && offsetInLine <= range.to) {
          event.preventDefault();
          callbackRef.current(range.key);
          return true;
        }
      }
      return false;
    },
  });

  return [plugin, clickHandler];
}

/**
 * The email body code editor: CodeMirror with HTML highlighting. Fragment
 * references (`{% include/extends 'key' %}`) are highlighted as chips and
 * Cmd/Ctrl-clickable to open that building block. A tip line explains the
 * interaction when references are present. Exposes an imperative
 * `insertAtCursor` for the toolbar's insert actions.
 */
const TwigCodeEditor = forwardRef<TwigCodeEditorHandle, TwigCodeEditorProps>(
  (
    {
      value,
      onChange,
      readOnly,
      placeholder,
      minHeight = '380px',
      maxHeight,
      onOpenReference,
      variableGroups,
      onSelectionChange,
    },
    ref,
  ) => {
    const intl = useIntl();
    const cmRef = useRef<ReactCodeMirrorRef>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    /** Caret coordinates for the suggestion list, in viewport space. */
    const [anchor, setAnchor] = useState<SuggestionAnchor | null>(null);

    /**
     * No language of its own — this is the source of a template, not a preview of one —
     * so each saved text is listed with whatever wording it happens to have.
     */
    const translationEntries = useTranslationEntries();

    const variables = useVariableSuggestions({
      variableGroups,
      translations: translationEntries,
      enabled: !readOnly,
      onAccept: (option, trigger) => {
        const view = cmRef.current?.view;
        if (!view) return;
        const { snippet } = option;
        view.dispatch({
          changes: { from: trigger.start, to: trigger.end, insert: snippet },
          selection: { anchor: trigger.start + snippet.length },
        });
        view.focus();
      },
    });

    /**
     * Reports the caret to the suggestion hook and measures where the list should
     * appear. Positions are document offsets, so the window handed over starts at
     * `from` — the hook adds that back.
     */
    const syncFromView = useCallback(
      (view: EditorView) => {
        const head = view.state.selection.main.head;
        const from = Math.max(0, head - TRIGGER_LOOKBACK);
        variables.sync(view.state.doc.sliceString(from, head), from);

        // Handed over as the caret's viewport box: the list is portalled to the
        // body, so it does the offsetting and the edge-clamping itself.
        const coords = view.coordsAtPos(head);
        if (!coords) return;
        setAnchor({
          left: coords.left,
          top: coords.top,
          bottom: coords.bottom,
        });
      },
      [variables],
    );

    /**
     * The extensions are built once per mount, so the live handlers reach them
     * through refs rather than by rebuilding the editor on every render.
     */
    const syncRef = useRef(syncFromView);
    syncRef.current = syncFromView;
    const keyRef = useRef(variables.handleKey);
    keyRef.current = variables.handleKey;
    const closeRef = useRef(variables.close);
    closeRef.current = variables.close;

    /**
     * The list is fixed-positioned, so anything that moves the caret on screen without
     * editing the text — the editor or a panel around it scrolling, the window
     * resizing — has to re-measure or the card detaches from the word it belongs to.
     * Capture phase: the scroll happens on an ancestor container and does not bubble.
     */
    useEffect(() => {
      if (!variables.open) return undefined;
      const reanchor = () => {
        const view = cmRef.current?.view;
        if (view) syncRef.current(view);
      };
      window.addEventListener('scroll', reanchor, true);
      window.addEventListener('resize', reanchor);
      return () => {
        window.removeEventListener('scroll', reanchor, true);
        window.removeEventListener('resize', reanchor);
      };
    }, [variables.open]);

    // Keep the open-callback fresh without rebuilding the editor.
    const callbackRef = useRef<((key: string) => void) | undefined>(
      onOpenReference,
    );
    callbackRef.current = onOpenReference;

    const hoverHint = getIntlTranslation(intl, {
      id: 'pages.emailCenter.editor.openReferenceHint',
      defaultMessage: '⌘/Ctrl-click to open this building block',
    });

    const extensions = useMemo(
      () => [
        // HTML highlighting (email bodies are HTML with Twig control tags);
        // the Twig references get their own clickable decoration below.
        html(),
        EditorView.lineWrapping,
        fragmentReferenceExtension(callbackRef, hoverHint),
        EditorView.updateListener.of((update) => {
          if (update.docChanged || update.selectionSet) {
            syncRef.current(update.view);
          }
        }),
        // Highest precedence: while the list is up, the arrows and Enter belong
        // to it rather than to the editor's own keymap. Returning true is what
        // stops CodeMirror handling the key as well.
        Prec.highest(
          EditorView.domEventHandlers({
            keydown: (event) => keyRef.current(event.key),
            blur: () => {
              closeRef.current();
              return false;
            },
          }),
        ),
      ],
      [hoverHint],
    );

    useImperativeHandle(ref, () => ({
      insertAtCursor(snippet: string) {
        const view = cmRef.current?.view;
        if (!view) return;
        const { from, to } = view.state.selection.main;
        view.dispatch({
          changes: { from, to, insert: snippet },
          selection: { anchor: from + snippet.length },
        });
        view.focus();
      },
      reveal(from: number, to: number) {
        const view = cmRef.current?.view;
        if (!view) return;
        // Clamped: the caller's offsets come from the source string it last saw,
        // which can be a keystroke behind the document.
        const end = Math.min(to, view.state.doc.length);
        view.dispatch({
          selection: { anchor: Math.min(from, end), head: end },
          scrollIntoView: true,
        });
        view.focus();
      },
    }));

    /**
     * Reports the selection to the host. `selectionSet` alone is not enough —
     * typing inside a selection replaces it, which changes what is highlighted
     * without counting as a selection event.
     */
    const handleUpdate = useCallback(
      (update: ViewUpdate) => {
        if (!onSelectionChange) return;
        if (!update.selectionSet && !update.docChanged) return;
        const { from, to } = update.state.selection.main;
        onSelectionChange({
          from,
          to,
          text: update.state.doc.sliceString(from, to),
        });
      },
      [onSelectionChange],
    );

    const hasReferences = findFragmentReferenceRanges(value ?? '').length > 0;

    return (
      <div
        ref={wrapRef}
        className={`${styles.twigEditorWrap}${
          readOnly ? ` ${styles.readOnly}` : ''
        }`}
      >
        <CodeMirror
          ref={cmRef}
          className={styles.cmEditor}
          value={value}
          readOnly={readOnly}
          editable={!readOnly}
          minHeight={minHeight}
          maxHeight={maxHeight}
          basicSetup={{ foldGutter: false, highlightActiveLine: !readOnly }}
          placeholder={placeholder}
          extensions={extensions}
          onChange={(next) => onChange?.(next)}
          onUpdate={handleUpdate}
        />
        {/* Follows the caret: CodeMirror gives no DOM node an antd popup could
            hang on, so the coordinates are measured and the card placed by hand. */}
        <VariableSuggestionPopup
          anchor={variables.open ? anchor : null}
          suggestions={variables.suggestions}
          activeIndex={variables.activeIndex}
          onPick={variables.accept}
        />

        {onOpenReference && hasReferences && (
          <div className={styles.referenceTip}>
            {getIntlTranslation(intl, {
              id: 'pages.emailCenter.editor.openReferenceTip',
              defaultMessage:
                'Tip: ⌘/Ctrl-click a highlighted building block to open it.',
            })}
          </div>
        )}
      </div>
    );
  },
);

TwigCodeEditor.displayName = 'TwigCodeEditor';

export default TwigCodeEditor;
